/**
 * Save to PRISM (TaskBrief "Saving and opening"; ADR 0024, 0025): the Save to PRISM button and host:request-save,
 * the save dialog (name, description, tags, folder), the conflict prompt (who saved last and when; Save as copy,
 * Overwrite, Cancel) and host:request-close with unsaved changes (Save, Discard, Cancel). Every answer to a host
 * request echoes its requestId.
 */
import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { ApiError, api, type PrismCollection, type SaveRequest, type VersionConflict } from '../api.ts';
import { t } from '../i18n.ts';
import { Modal } from '../panels/Dialogs.tsx';
import type { EditorStore } from '../state/store.ts';
import { prism } from './session.ts';

type Pending = { requestId?: string; closeAfter?: { requestId?: string } };

export type FlowState =
  | { kind: 'idle' }
  | ({ kind: 'dialog'; isNew: boolean; busy: boolean; error?: string; errors?: Array<{ code: string; message: string }> } & Pending)
  | ({ kind: 'saving' } & Pending)
  | ({ kind: 'conflict'; conflict: VersionConflict; last: SaveRequest; busy: boolean; error?: string } & Pending)
  | { kind: 'close'; requestId?: string };

export interface SaveOutcome {
  ok: boolean;
}

/** The save flow of one open draft (one per editor). */
export class SaveFlow {
  readonly store: EditorStore;
  state: FlowState = { kind: 'idle' };
  #listeners = new Set<() => void>();

  constructor(store: EditorStore) {
    this.store = store;
  }

  subscribe = (fn: () => void) => {
    this.#listeners.add(fn);
    return () => void this.#listeners.delete(fn);
  };
  getState = () => this.state;
  #set(s: FlowState): void {
    this.state = s;
    for (const l of this.#listeners) l();
  }

  get inPrism(): boolean {
    return !!this.store.state.prism.assetId;
  }

  /** Save to PRISM: the button (no requestId) or host:request-save. */
  start(p: Pending = {}): void {
    if (this.state.kind !== 'idle' && this.state.kind !== 'close') {
      // Already saving or asking: a second host request is refused (the host allows one pending request anyway).
      if (p.requestId) prism.bridge?.error({ code: 'SAVE_FAILED', message: 'A save is already in progress' }, p.requestId);
      return;
    }
    if (prism.state.status === 'expired') {
      if (p.requestId) prism.bridge?.error({ code: 'TOKEN_EXPIRED', message: 'The editor session expired' }, p.requestId);
      if (p.closeAfter) this.#closeCancelled(p.closeAfter.requestId);
      return;
    }
    // A requested save of a graphic that is already in PRISM needs no questions: the next version.
    if (this.inPrism && p.requestId) {
      void this.submit({ mode: 'version' }, p);
      return;
    }
    this.#set({ kind: 'dialog', isNew: !this.inPrism, busy: false, ...p });
  }

  #pending(): Pending {
    const s = this.state;
    return s.kind === 'dialog' || s.kind === 'conflict' || s.kind === 'saving' ? { ...(s.requestId ? { requestId: s.requestId } : {}), ...(s.closeAfter ? { closeAfter: s.closeAfter } : {}) } : {};
  }

  /** Run a save: flush the autosave, then POST /drafts/:id/save; conflicts open the prompt. */
  async submit(req: SaveRequest, pending: Pending = this.#pending()): Promise<SaveOutcome> {
    const store = this.store;
    const s = this.state;
    if (s.kind === 'dialog') this.#set({ ...s, busy: true, error: undefined, errors: undefined });
    else if (s.kind === 'conflict') this.#set({ ...s, busy: true, error: undefined });
    else this.#set({ kind: 'saving', ...pending });
    try {
      await store.saveNow();
      if (store.state.save.status === 'conflict') throw new ApiError(409, 'REVISION_CONFLICT', t('prism.draftConflict'));
      const scene = store.document;
      const r = await api.saveToPrism(store.state.draftId, { ...req, baseRevision: store.state.revision });
      // Reloads the draft when it changed elsewhere during the save (draftChanged) and this tab has no edits since.
      await store.prismSaved(r, scene, req.description);
      prism.saved({ assetId: r.assetId, version: r.version, name: r.name }, pending.requestId);
      this.#set({ kind: 'idle' });
      if (pending.closeAfter) prism.bridge?.close({ saved: true }, pending.closeAfter.requestId);
      return { ok: true };
    } catch (e) {
      const err = e instanceof ApiError ? e : new ApiError(0, 'SAVE_FAILED', e instanceof Error ? e.message : String(e));
      if (err.code === 'VERSION_CONFLICT') {
        this.#set({ kind: 'conflict', conflict: err.details as unknown as VersionConflict, last: req, busy: false, ...pending });
        return { ok: false };
      }
      if (err.status === 401) prism.expire();
      // A host request gets its answer now; the user can still retry from the dialog.
      const code = err.status === 403 ? 'PERMISSION_DENIED' : err.status === 401 ? 'TOKEN_EXPIRED' : 'SAVE_FAILED';
      if (pending.requestId) prism.bridge?.error({ code, message: err.message }, pending.requestId);
      if (pending.closeAfter) this.#closeCancelled(pending.closeAfter.requestId);
      const errors = err.code === 'CHECK_FAILED' ? ((err.details?.errors as Array<{ code: string; message: string }> | undefined) ?? []) : undefined;
      const message = err.code === 'CHECK_FAILED' ? t('prism.checkFailed') : err.status === 403 ? t('prism.forbidden', { message: err.message }) : err.message;
      this.#set({ kind: 'dialog', isNew: !this.inPrism, busy: false, error: message, ...(errors ? { errors } : {}) });
      return { ok: false };
    }
  }

  /** The conflict prompt's answer. */
  resolve(choice: 'copy' | 'overwrite' | 'cancel', copyName?: string): void {
    const s = this.state;
    if (s.kind !== 'conflict') return;
    if (choice === 'cancel') {
      if (s.requestId) prism.bridge?.error({ code: 'VERSION_CONFLICT_UNRESOLVED', message: 'The conflict prompt was cancelled' }, s.requestId);
      if (s.closeAfter) this.#closeCancelled(s.closeAfter.requestId);
      this.#set({ kind: 'idle' });
      return;
    }
    const { mode: _m, expectedVersion: _e, collectionId: _c, ...rest } = s.last;
    void this.submit(choice === 'copy' ? { ...rest, mode: 'copy', ...(copyName ? { name: copyName } : {}) } : { ...rest, mode: 'overwrite', expectedVersion: s.conflict.currentVersion });
  }

  /** The save dialog was dismissed. */
  cancel(): void {
    const s = this.state;
    if (s.kind === 'dialog' && !s.busy) {
      if (s.requestId) prism.bridge?.error({ code: 'SAVE_CANCELLED', message: 'The user dismissed the save' }, s.requestId);
      if (s.closeAfter) this.#closeCancelled(s.closeAfter.requestId);
      this.#set({ kind: 'idle' });
    }
  }

  /** host:request-close (requestId) or the editor's own Close button. */
  requestClose(requestId?: string): void {
    if (!this.store.state.prism.dirty) {
      prism.bridge?.close({ saved: false }, requestId);
      return;
    }
    if (this.state.kind !== 'idle') {
      // Busy saving or asking: not now.
      this.#closeCancelled(requestId);
      return;
    }
    this.#set({ kind: 'close', ...(requestId ? { requestId } : {}) });
  }

  /** The unsaved-changes prompt's answer. */
  answerClose(choice: 'save' | 'discard' | 'cancel'): void {
    const s = this.state;
    if (s.kind !== 'close') return;
    this.#set({ kind: 'idle' });
    if (choice === 'cancel') this.#closeCancelled(s.requestId);
    else if (choice === 'discard') {
      // The changes are gone for good: the draft goes too, so reopening starts from PRISM's version (ADR 0032).
      void this.store.discard().finally(() => prism.bridge?.close({ saved: false }, s.requestId));
    }
    else if (this.inPrism) void this.submit({ mode: 'version' }, { closeAfter: { ...(s.requestId ? { requestId: s.requestId } : {}) } });
    else this.#set({ kind: 'dialog', isNew: true, busy: false, closeAfter: { ...(s.requestId ? { requestId: s.requestId } : {}) } });
  }

  #closeCancelled(requestId: string | undefined): void {
    if (requestId) prism.bridge?.closeCancelled(requestId);
  }
}

export function useFlow(flow: SaveFlow): FlowState {
  return useSyncExternalStore(flow.subscribe, flow.getState);
}

/** Wire the flow to the bridge (request-save, request-close, dirty) while the editor is open. */
export function usePrismBridge(flow: SaveFlow): void {
  useEffect(() => {
    if (!prism.embedded) return;
    const off = prism.handle({ save: (id) => flow.start({ requestId: id }), close: (id) => flow.requestClose(id) });
    let last = flow.store.state.prism.dirty;
    prism.dirty(last);
    const unsub = flow.store.subscribe(() => {
      const d = flow.store.state.prism.dirty;
      if (d !== last) {
        last = d;
        prism.dirty(d);
      }
    });
    return () => {
      off();
      unsub();
    };
  }, [flow]);
}

// ------------------------------------------------------------------------------------------------------------------
// UI

function when(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function SaveDialog({ flow, state }: { flow: SaveFlow; state: Extract<FlowState, { kind: 'dialog' }> }) {
  const scene = flow.store.document;
  const link = flow.store.state.prism;
  const [name, setName] = useState(scene.meta.name);
  const [description, setDescription] = useState(scene.meta.description ?? '');
  const [tags, setTags] = useState('');
  const [folder, setFolder] = useState(prism.state.init?.folderId ?? '');
  const [collections, setCollections] = useState<PrismCollection[]>();
  const canMeta = prism.can('metadata');
  useEffect(() => {
    if (!state.isNew) return;
    api.prismCollections().then((r) => setCollections(r.items), () => setCollections([]));
  }, [state.isNew]);
  const meta = (): Omit<SaveRequest, 'mode'> => ({
    ...(name.trim() ? { name: name.trim() } : {}),
    ...(canMeta && description !== (scene.meta.description ?? '') ? { description } : {}),
    ...(canMeta && tags.trim() ? { tags: tags.split(',').map((x) => x.trim()).filter(Boolean) } : {}),
  });
  const busy = state.busy;
  const submit = () => {
    if (!busy && name.trim()) void flow.submit(state.isNew ? { mode: 'new', ...meta(), ...(folder ? { collectionId: folder } : {}) } : { mode: 'version', ...meta() });
  };
  return (
    <Modal title={state.isNew ? t('prism.saveNewTitle') : t('prism.saveVersionTitle', { version: (link.version ?? 0) + 1 })} onClose={busy ? undefined : () => flow.cancel()} testId="save-dialog" labelledBy="save-title">
      {/* Not a <form>: PRISM's iframe sandbox has no allow-forms, so a form could not be submitted. */}
      <div
        className="save-form"
        role="group"
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !(e.target instanceof HTMLTextAreaElement) && !(e.target instanceof HTMLButtonElement)) {
            e.preventDefault();
            submit();
          }
        }}
      >
        <label className="field text">
          <span className="field-label">{t('prism.name')}</span>
          <input type="text" value={name} maxLength={200} required onChange={(e) => setName(e.target.value)} data-testid="save-name" disabled={busy} />
        </label>
        <label className="field text">
          <span className="field-label">{t('prism.description')}</span>
          <textarea value={description} maxLength={2000} rows={2} onChange={(e) => setDescription(e.target.value)} data-testid="save-description" disabled={busy || !canMeta} />
        </label>
        <label className="field text">
          <span className="field-label">{t('prism.tags')}</span>
          <input type="text" value={tags} placeholder={t('prism.tagsHint')} onChange={(e) => setTags(e.target.value)} data-testid="save-tags" disabled={busy || !canMeta} />
        </label>
        {!canMeta && <p className="hint">{t('prism.noMetadataPermission')}</p>}
        {state.isNew && (
          <label className="field select">
            <span className="field-label">{t('prism.folder')}</span>
            <select value={folder} onChange={(e) => setFolder(e.target.value)} data-testid="save-folder" disabled={busy}>
              <option value="">{t('prism.noFolder')}</option>
              {(collections ?? []).map((c) => (
                <option key={c.id} value={c.id} disabled={!c.canEdit}>
                  {c.name}
                  {c.canEdit ? '' : ` (${t('prism.readOnlyFolder')})`}
                </option>
              ))}
            </select>
          </label>
        )}
        {busy && (
          <p className="hint" role="status" data-testid="save-busy">
            {t('prism.saving')}
          </p>
        )}
        {state.error && (
          <div className="error" role="alert" data-testid="save-error">
            <p>{state.error}</p>
            {state.errors && state.errors.length > 0 && (
              <ul className="check-issues">
                {state.errors.map((x, i) => (
                  <li key={i} className="err">
                    <code>{x.code}</code> {x.message}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        <div className="modal-actions">
          <button type="button" className="btn" onClick={() => flow.cancel()} disabled={busy} data-testid="save-cancel">
            {t('prism.cancel')}
          </button>
          {!state.isNew && (
            <button type="button" className="btn" disabled={busy} onClick={() => void flow.submit({ mode: 'copy', ...meta() })} data-testid="save-copy">
              {t('prism.saveAsCopy')}
            </button>
          )}
          <button type="button" className="btn primary" disabled={busy || !name.trim()} onClick={submit} data-testid="save-submit">
            {state.isNew ? t('prism.saveNew') : t('prism.saveVersion', { version: (link.version ?? 0) + 1 })}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ConflictPrompt({ flow, state }: { flow: SaveFlow; state: Extract<FlowState, { kind: 'conflict' }> }) {
  const c = state.conflict;
  const base = flow.store.state.prism.version;
  const [copyName, setCopyName] = useState(`${flow.store.document.meta.name} (copy)`);
  return (
    <Modal title={t('prism.conflictTitle')} testId="conflict-prompt" labelledBy="prism-conflict-title">
      <p data-testid="conflict-who">
        {t('prism.conflictBody', { who: c.savedBy?.displayName ?? t('prism.someone'), version: c.currentVersion, when: when(c.savedAt), base: base ?? '?' })}
      </p>
      <label className="field text">
        <span className="field-label">{t('prism.copyName')}</span>
        <input type="text" value={copyName} maxLength={200} onChange={(e) => setCopyName(e.target.value)} data-testid="conflict-copy-name" disabled={state.busy} />
      </label>
      {state.busy && <p className="hint">{t('prism.saving')}</p>}
      <div className="modal-actions">
        <button type="button" className="btn" disabled={state.busy} onClick={() => flow.resolve('cancel')} data-testid="conflict-cancel">
          {t('prism.cancel')}
        </button>
        <button type="button" className="btn" disabled={state.busy} onClick={() => flow.resolve('overwrite')} data-testid="conflict-overwrite-prism">
          {t('prism.overwrite', { version: c.currentVersion + 1 })}
        </button>
        <button type="button" className="btn primary" disabled={state.busy || !copyName.trim()} onClick={() => flow.resolve('copy', copyName.trim())} data-testid="conflict-copy">
          {t('prism.saveAsCopy')}
        </button>
      </div>
    </Modal>
  );
}

function ClosePrompt({ flow }: { flow: SaveFlow }) {
  return (
    <Modal title={t('prism.closeTitle')} onClose={() => flow.answerClose('cancel')} testId="close-prompt" labelledBy="close-title">
      <p>{t('prism.closeBody')}</p>
      <div className="modal-actions">
        <button type="button" className="btn" onClick={() => flow.answerClose('cancel')} data-testid="close-cancel">
          {t('prism.cancel')}
        </button>
        <button type="button" className="btn danger" onClick={() => flow.answerClose('discard')} data-testid="close-discard">
          {t('prism.discard')}
        </button>
        <button type="button" className="btn primary" onClick={() => flow.answerClose('save')} data-testid="close-save">
          {t('prism.saveAndClose')}
        </button>
      </div>
    </Modal>
  );
}

/** The dialogs of the flow (rendered by the editor). */
export function SaveFlowDialogs({ flow }: { flow: SaveFlow }) {
  const state = useFlow(flow);
  usePrismBridge(flow);
  switch (state.kind) {
    case 'dialog':
      return <SaveDialog flow={flow} state={state} />;
    case 'conflict':
      return <ConflictPrompt flow={flow} state={state} />;
    case 'close':
      return <ClosePrompt flow={flow} />;
    case 'saving':
      return (
        <Modal title={t('prism.savingTitle')} testId="save-progress" labelledBy="saving-title">
          <p className="hint" role="status">
            {t('prism.saving')}
          </p>
        </Modal>
      );
    default:
      return null;
  }
}

export function useSaveFlow(store: EditorStore): SaveFlow {
  return useMemo(() => new SaveFlow(store), [store]);
}
